import React, { useEffect, useState, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import {
  MessageCircle, X, Minimize2, Maximize2, Send, Hash, User,
  ChevronLeft, Plus, Search, Users, Circle, Download, FileText
} from "lucide-react";

// Chat bubble component
function ChatBubble({ message, isOwn, showSender = true }) {
  return (
    <div className={`flex ${isOwn ? "justify-end" : "justify-start"} mb-2`}>
      <div className={`max-w-[80%]`}>
        {!isOwn && showSender && (
          <p className="text-[10px] text-slate-400 mb-0.5 ml-2">{message.senderName}</p>
        )}
        <div
          className={`px-3 py-2 rounded-2xl text-sm ${
            isOwn
              ? "bg-[#0A1C3A] text-white rounded-br-sm"
              : "bg-white border border-slate-200 text-slate-900 rounded-bl-sm"
          }`}
        >
          {message.content}
        </div>
        <p className={`text-[9px] text-slate-300 mt-0.5 ${isOwn ? "text-right mr-1" : "ml-2"}`}>
          {new Date(message.createdAt).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })}
        </p>
      </div>
    </div>
  );
}

// Conversation item in list
function ConversationItem({ item, type, isActive, onClick, unreadCount }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-all ${
        isActive ? "bg-[#0A1C3A] text-white" : "hover:bg-slate-100"
      }`}
    >
      {type === "channel" ? (
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive ? "bg-white/20" : "bg-slate-100"}`}>
          <Hash className={`h-4 w-4 ${isActive ? "text-white" : "text-slate-500"}`} />
        </div>
      ) : (
        <Avatar className="h-8 w-8">
          <AvatarFallback className={`text-xs ${isActive ? "bg-white/20 text-white" : "bg-slate-200"}`}>
            {item.name?.charAt(0)?.toUpperCase() || "?"}
          </AvatarFallback>
        </Avatar>
      )}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${isActive ? "text-white" : "text-slate-900"}`}>
          {type === "channel" ? `#${item.name}` : item.name}
        </p>
        {item.lastMessage && (
          <p className={`text-xs truncate ${isActive ? "text-white/60" : "text-slate-400"}`}>
            {item.lastMessage}
          </p>
        )}
      </div>
      {unreadCount > 0 && (
        <Badge className="bg-[#E07A13] text-white text-[10px] px-1.5 py-0 min-w-[18px] h-[18px] flex items-center justify-center">
          {unreadCount > 99 ? "99+" : unreadCount}
        </Badge>
      )}
    </button>
  );
}

export function FloatingChat({ session }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [view, setView] = useState("list"); // "list" | "chat"
  const [activeConversation, setActiveConversation] = useState(null); // { type: "channel" | "dm", data: {...} }
  
  const [channels, setChannels] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [showNewDm, setShowNewDm] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const prevUnreadRef = useRef(0);
  const audioRef = useRef(null);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    try {
      // Create audio element on demand
      if (!audioRef.current) {
        audioRef.current = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleyo+r+PsqlMdLqDT7uSlUSA0qd3x66RLGjKo4PLrnEUYMa7k8+qXPxcwr+Xy6JY7Fy+t5PPoljwXL63k8+iWPBcvreTz6JY8Fy+t5PPolzwXL63k8+iWOxcvreTz6Jc8Fy+t5PPoljwXL63k8+iWOxcvreTz6Jc8Fy+t5PPolzwXL63k8+iWPBcvreTz6JY8");
        audioRef.current.volume = 0.3;
      }
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    } catch (e) {
      // Ignore audio errors
    }
  }, []);

  // Load channels and conversations
  const loadData = useCallback(async () => {
    try {
      const [channelsRes, convsRes, usersRes] = await Promise.all([
        api.get("/chat/channels"),
        api.get("/chat/conversations"),
        api.get("/users/list")
      ]);
      
      const channelsList = channelsRes.data.channels || [];
      const convsList = convsRes.data.conversations || [];
      
      setChannels(channelsList);
      setConversations(convsList);
      setUsers(usersRes.data.users || []);
      
      // Calculate total unread
      const channelUnread = channelsList.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
      const dmUnread = convsList.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
      const newTotalUnread = channelUnread + dmUnread;
      
      // Show notification for new messages (only if unread increased and chat is closed/minimized)
      if (newTotalUnread > prevUnreadRef.current && (!isOpen || isMinimized)) {
        const diff = newTotalUnread - prevUnreadRef.current;
        toast.info(`${diff} nowa wiadomość${diff > 1 ? 'i' : ''} w czacie`, {
          description: "Kliknij ikonę czatu, aby przeczytać",
          duration: 4000,
        });
        playNotificationSound();
      }
      
      prevUnreadRef.current = newTotalUnread;
      setTotalUnread(newTotalUnread);
      
    } catch (e) {
      console.error("Failed to load chat data:", e);
    }
  }, [isOpen, isMinimized, playNotificationSound]);

  // Poll for updates
  useEffect(() => {
    if (!session?.user) return;
    
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [session, loadData]);

  // Load messages when conversation changes
  useEffect(() => {
    if (!activeConversation) {
      setMessages([]);
      return;
    }

    const loadMessages = async () => {
      try {
        let res;
        if (activeConversation.type === "channel") {
          res = await api.get("/chat/messages", { params: { channelId: activeConversation.data.id } });
        } else {
          res = await api.get("/chat/messages", { params: { recipientId: activeConversation.data.userId } });
        }
        setMessages(res.data.messages || []);
      } catch (e) {
        console.error("Failed to load messages:", e);
      }
    };

    loadMessages();
    
    // Poll messages when chat is open
    const interval = setInterval(loadMessages, 15000);
    return () => clearInterval(interval);
  }, [activeConversation]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus input when opening chat
  useEffect(() => {
    if (view === "chat" && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [view, isMinimized]);

  // Send message
  const sendMessage = async () => {
    if (!newMessage.trim() || !activeConversation) return;
    
    setSending(true);
    try {
      const payload = {
        content: newMessage.trim(),
        ...(activeConversation.type === "channel" 
          ? { channelId: activeConversation.data.id }
          : { recipientId: activeConversation.data.userId }
        )
      };
      
      const res = await api.post("/chat/messages", payload);
      setMessages([...messages, res.data.data]);
      setNewMessage("");
    } catch (e) {
      toast.error("Błąd wysyłania wiadomości");
    } finally {
      setSending(false);
    }
  };

  // Open conversation
  const openConversation = async (type, data) => {
    setActiveConversation({ type, data });
    setView("chat");
    
    // Mark messages as read
    try {
      if (type === "channel") {
        await api.post("/chat/mark-read", null, { params: { channelId: data.id } });
      } else {
        await api.post("/chat/mark-read", null, { params: { recipientId: data.userId } });
      }
      // Reload data to update unread counts
      loadData();
    } catch (e) {
      console.error("Failed to mark messages as read:", e);
    }
    setShowNewDm(false);
  };

  // Start new DM
  const startNewDm = (user) => {
    openConversation("dm", {
      userId: user.id,
      name: user.fullName || user.email
    });
  };

  // Back to list
  const goBack = () => {
    setView("list");
    setActiveConversation(null);
    setShowNewDm(false);
  };

  // Export chat to PDF
  const exportToPdf = async () => {
    if (!messages.length || !activeConversation) {
      toast.error("Brak wiadomości do eksportu");
      return;
    }
    
    setExporting(true);
    try {
      const conversationName = getConversationName();
      const now = new Date().toLocaleString("pl-PL");
      
      // Generate HTML content for PDF
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Rozmowa: ${conversationName}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            h1 { color: #0A1C3A; border-bottom: 2px solid #E07A13; padding-bottom: 10px; }
            .meta { color: #666; font-size: 12px; margin-bottom: 30px; }
            .message { margin-bottom: 15px; padding: 10px; border-radius: 8px; }
            .message.own { background: #0A1C3A; color: white; margin-left: 20%; }
            .message.other { background: #f5f5f5; margin-right: 20%; }
            .sender { font-weight: bold; font-size: 12px; margin-bottom: 5px; }
            .content { margin-bottom: 5px; }
            .time { font-size: 10px; color: #999; }
            .message.own .time { color: rgba(255,255,255,0.6); }
          </style>
        </head>
        <body>
          <h1>${activeConversation.type === "channel" ? "Kanał" : "Rozmowa"}: ${conversationName}</h1>
          <div class="meta">Eksportowano: ${now} | Wiadomości: ${messages.length}</div>
          ${messages.map(msg => `
            <div class="message ${msg.senderId === session?.user?.id ? 'own' : 'other'}">
              <div class="sender">${msg.senderName || 'Nieznany'}</div>
              <div class="content">${msg.content}</div>
              <div class="time">${new Date(msg.createdAt).toLocaleString("pl-PL")}</div>
            </div>
          `).join('')}
        </body>
        </html>
      `;
      
      // Create blob and trigger download
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chat_${conversationName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().slice(0,10)}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("Rozmowa wyeksportowana!");
    } catch (e) {
      toast.error("Błąd eksportu rozmowy");
    } finally {
      setExporting(false);
    }
  };

  // Filter items by search
  const filteredChannels = channels.filter(c => 
    c.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredConversations = conversations.filter(c =>
    c.userName?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredUsers = users.filter(u => 
    u.id !== session?.user?.id &&
    (u.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
     u.email?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Get conversation name
  const getConversationName = () => {
    if (!activeConversation) return "";
    if (activeConversation.type === "channel") {
      return `#${activeConversation.data.name}`;
    }
    return activeConversation.data.name || activeConversation.data.userName;
  };

  if (!session?.user) return null;

  return (
    <div className="fixed bottom-20 right-4 z-[9999]" data-testid="floating-chat">
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="w-14 h-14 rounded-full bg-[#0A1C3A] text-white shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center justify-center relative"
          data-testid="floating-chat-button"
        >
          <MessageCircle className="h-6 w-6" />
          {totalUnread > 0 && (
            <span className="absolute -top-1 -right-1 bg-[#E07A13] text-white text-xs font-bold min-w-[20px] h-5 rounded-full flex items-center justify-center px-1">
              {totalUnread > 99 ? "99+" : totalUnread}
            </span>
          )}
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className={`bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden transition-all ${
            isMinimized ? "w-72 h-14" : "w-80 sm:w-96 h-[480px]"
          }`}
          style={{ maxHeight: "calc(100vh - 140px)" }}
        >
          {/* Header */}
          <div className="bg-[#0A1C3A] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {view === "chat" && !isMinimized && (
                <button onClick={goBack} className="hover:bg-white/10 p-1 rounded">
                  <ChevronLeft className="h-5 w-5" />
                </button>
              )}
              <div className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                <span className="font-medium text-sm truncate max-w-[150px]">
                  {view === "chat" ? getConversationName() : "Czat zespołowy"}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {/* Export button - only show in chat view */}
              {view === "chat" && !isMinimized && messages.length > 0 && (
                <button
                  onClick={exportToPdf}
                  disabled={exporting}
                  className="p-1.5 hover:bg-white/10 rounded transition-colors"
                  title="Eksportuj rozmowę"
                >
                  <Download className={`h-4 w-4 ${exporting ? 'animate-pulse' : ''}`} />
                </button>
              )}
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-1.5 hover:bg-white/10 rounded transition-colors"
              >
                {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
              </button>
              <button
                onClick={() => { setIsOpen(false); setIsMinimized(false); setView("list"); }}
                className="p-1.5 hover:bg-white/10 rounded transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Content */}
          {!isMinimized && (
            <div className="flex flex-col h-[calc(100%-56px)]">
              {view === "list" ? (
                <>
                  {/* Search */}
                  <div className="p-3 border-b">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Szukaj..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 h-9 rounded-full text-sm"
                      />
                    </div>
                  </div>

                  {/* Conversations List */}
                  <ScrollArea className="flex-1">
                    <div className="p-2">
                      {/* New DM Button */}
                      <button
                        onClick={() => setShowNewDm(!showNewDm)}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left hover:bg-slate-100 mb-2 border border-dashed border-slate-300"
                      >
                        <Plus className="h-4 w-4 text-slate-500" />
                        <span className="text-sm text-slate-600">Nowa rozmowa</span>
                      </button>

                      {/* New DM User List */}
                      {showNewDm && (
                        <div className="mb-3 bg-slate-50 rounded-lg p-2">
                          <p className="text-xs text-slate-500 mb-2 px-2">Wybierz osobę:</p>
                          {filteredUsers.map(user => (
                            <button
                              key={user.id}
                              onClick={() => startNewDm(user)}
                              className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white text-left"
                            >
                              <Avatar className="h-6 w-6">
                                <AvatarFallback className="text-[10px] bg-slate-200">
                                  {(user.fullName || user.email)?.charAt(0)?.toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-sm truncate">{user.fullName || user.email}</span>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Channels */}
                      {filteredChannels.length > 0 && (
                        <>
                          <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider px-3 py-1">
                            Kanały
                          </p>
                          {filteredChannels.map(channel => (
                            <ConversationItem
                              key={channel.id}
                              item={{ name: channel.name, lastMessage: "" }}
                              type="channel"
                              isActive={activeConversation?.data?.id === channel.id}
                              onClick={() => openConversation("channel", channel)}
                              unreadCount={channel.unreadCount}
                            />
                          ))}
                        </>
                      )}

                      {/* Direct Messages */}
                      {filteredConversations.length > 0 && (
                        <>
                          <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider px-3 py-1 mt-2">
                            Wiadomości
                          </p>
                          {filteredConversations.map(conv => (
                            <ConversationItem
                              key={conv.userId}
                              item={{ name: conv.userName, lastMessage: conv.lastMessage }}
                              type="dm"
                              isActive={activeConversation?.data?.userId === conv.userId}
                              onClick={() => openConversation("dm", { userId: conv.userId, name: conv.userName })}
                              unreadCount={conv.unreadCount}
                            />
                          ))}
                        </>
                      )}

                      {filteredChannels.length === 0 && filteredConversations.length === 0 && !showNewDm && (
                        <div className="text-center py-8 text-slate-400">
                          <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">Brak rozmów</p>
                          <p className="text-xs">Kliknij &quot;Nowa rozmowa&quot;</p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </>
              ) : (
                <>
                  {/* Messages */}
                  <ScrollArea className="flex-1 p-3 bg-slate-50">
                    {messages.length === 0 ? (
                      <div className="text-center py-8 text-slate-400">
                        <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-30" />
                        <p className="text-sm">Brak wiadomości</p>
                        <p className="text-xs">Napisz pierwszą wiadomość!</p>
                      </div>
                    ) : (
                      messages.map(msg => (
                        <ChatBubble
                          key={msg.id}
                          message={msg}
                          isOwn={msg.senderId === session?.user?.id}
                          showSender={activeConversation?.type === "channel"}
                        />
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </ScrollArea>

                  {/* Input */}
                  <div className="p-3 border-t bg-white">
                    <div className="flex gap-2">
                      <Input
                        ref={inputRef}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                        placeholder="Napisz wiadomość..."
                        className="flex-1 h-9 rounded-full text-sm"
                        disabled={sending}
                        data-testid="floating-chat-input"
                      />
                      <Button
                        onClick={sendMessage}
                        disabled={!newMessage.trim() || sending}
                        size="sm"
                        className="h-9 w-9 p-0 rounded-full bg-[#0A1C3A] hover:bg-[#07152d]"
                        data-testid="floating-chat-send"
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
